import { Component, inject, Inject, signal, viewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AbbSidePanelComponent } from '@components/abb-side-panel/abb-side-panel.component';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import { AbbCommonModule } from 'app/abb-common.module';
import { ReportFormComponent } from '../report-form/report-form.component';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import {
  CreateReportMutation,
  CreateReportMutationVariables,
} from 'gql/graphql';
import { createReportMutation } from '@graphql/report';
import { combineDateTime, combineDateTimeLocale } from '@utils/datetime';
import { OutputSignalContent } from '@utils/angular-ext';
import { URLS } from '@constants/urls';
import { t } from '@utils/strings';
import { map, race, shareReplay, tap, timeout, timer } from 'rxjs';
import { OverlayService } from '@services/ui/overlay.service';

type ReportsSidePanelData = {
  mode: 'new';
};

@Component({
  selector: 'abb-reports-side-panel',
  standalone: true,
  template: `
    <abb-side-panel id="new-report-panel" [title]="'New Report' | t" #panelRef>
      <abb-report-form
        [mode]="data.mode"
        (formSubmit)="onSubmit($event)"
        (formValidationStatusChange)="handleSubmitStatusBtn($event)"
      ></abb-report-form>
      <div footer class="w-full flex flex-row gap-8 justify-end p-8">
        <abb-button
          variant="clean"
          [disabled]="saving()"
          (onclick)="onCancel()"
          >{{ 'cancel' | t }}</abb-button
        >
        <abb-button
          class="w-32"
          type="submit"
          form="report-form"
          [disabled]="submitBtnDisabled || saving()"
          [loading]="saving()"
          >{{ 'Create' | t }}</abb-button
        >
      </div>
    </abb-side-panel>
  `,
  styles: ``,
  imports: [AbbCommonModule, AbbSidePanelComponent, ReportFormComponent, TPipe],
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'reports',
    },
  ],
})
export class ReportsSidePanelComponent
  implements SidePanelData<ReportsSidePanelData>
{
  panelRef = viewChild<AbbSidePanelComponent>('panelRef');
  saving = signal(false);

  prefix = inject(TRANSLATION_PREFIX);
  constructor(
    @Inject(SIDE_PANEL_DATA) public data: ReportsSidePanelData,
    private readonly apollo: Apollo,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly overlayService: OverlayService
  ) {}

  submitBtnDisabled: boolean = true;
  handleSubmitStatusBtn(disabled: boolean): void {
    this.submitBtnDisabled = disabled;
  }

  onCancel() {
    this.panelRef()?.dispose();
  }

  onSubmit({
    form,
    isCustomTemplate,
  }: OutputSignalContent<ReportFormComponent['formSubmit']>): void {
    // FIXME: this should be done inside the form, not here. Even in pure HTML forms, onsubmit is only dispatched
    // only if the form is valid
    if (form.invalid) {
      return;
    }
    let fromDate: Date | undefined = undefined;
    let toDate: Date | undefined = undefined;

    if (form.value.interval!.fromDate && form.value.interval!.toDate) {
      fromDate = combineDateTimeLocale(
        form.value.interval!.fromDate,
        form.value.interval!.fromTime ?? ''
      );
      toDate = combineDateTimeLocale(
        form.value.interval!.toDate,
        form.value.interval!.toTime ?? ''
      );
    }
    if (isCustomTemplate) {
      this.onCancel();
      window.open(
        URLS.REPORT_TEMPLATE_PRINT_PAGE.replace(
          ':reportId',
          encodeURIComponent(form.value.categoryId!)
        ).replace(':reportTitle', encodeURIComponent(form.value.name!)),
        // .replace(
        //   ':properties',
        //   form.value.properties!.map((p) => encodeURIComponent(p)).join(',')
        // ),
        // .replace(':fromDate', fromDate.toISOString())
        // .replace(':toDate', toDate.toISOString()),
        '_blank'
      );
      return;
    }

    this.saving.set(true);

    const mutation = this.apollo
      .mutate<CreateReportMutation, CreateReportMutationVariables>({
        mutation: createReportMutation,
        variables: {
          input: {
            name: form.value.name!,
            categoryId: form.value.categoryId!,
            fromDate,
            toDate,
            properties: form.value.properties!,
          },
        },
      })
      .pipe(shareReplay(1));

    mutation.subscribe({
      next: () =>
        this.apollo.client.refetchQueries({
          include: ['ReportList'],
        }),
    });

    race([
      mutation.pipe(map(() => 'completed' as const)),
      timer(28_000).pipe(map(() => 'still-processing' as const)),
    ]).subscribe({
      next: (status) => {
        this.saving.set(false);
        this.panelRef()?.dispose();

        switch (status) {
          case 'completed': {
            this.toastService.notifySuccess(
              t(
                this.prefix,
                `{name} was created successfully`,
                this.translate,
                {
                  name: form.value.name,
                }
              )
            );
            break;
          }
          case 'still-processing': {
            this.overlayService.openAlert({
              title: this.translate.instant('reports.Processing'),
              message: this.translate.instant(
                'reports.The report will be ready in a few minutes. You can come back to this page later to see it'
              ),
            });
            break;
          }
        }
      },
      error: () => {
        this.saving.set(false);
        this.toastService.notifyError(
          t(
            this.prefix,
            `An error occurred while creating {name}`,
            this.translate,
            { name: form.value.name }
          )
        );
      },
    });
  }
}
